// apps/console/src/hooks/live-channel-socket.ts
//
// Pure, transport-agnostic helpers for the realtime live channel. These build
// the §6 protocol frames the broker expects and own the snapshot-seq dedup
// (§8 persist-then-fanout duplicate window). No React, no WebSocket here — the
// hook (useLiveChannel) wires these into a real `new WebSocket(url)`.
//
// NEVER import any @aws-sdk Cognito client here (it SIGKILLs the vitest CI
// workers — see apps/console CLAUDE.md). The operator JWT comes only from
// localStorage via the auth service's getAccessToken().

import { PROTOCOL_VERSION, type Envelope } from '@libs/realtime'

/** A monotonic-enough client frame id for correlation. */
let frameCounter = 0

export function nextFrameId(): string {
  frameCounter = (frameCounter + 1) % 1_000_000

  return `c-${Date.now()}-${frameCounter.toString(36)}`
}

/** The §6 first-message auth frame an operator sends on open. */
export function authFrame(jwt: string): Envelope<{ jwt: string }> {
  return { v: PROTOCOL_VERSION, id: nextFrameId(), type: 'auth', ts: Date.now(), payload: { jwt } }
}

/** A subscribe frame for one topic. */
export function subscribeFrame(topic: string): Envelope {
  return { v: PROTOCOL_VERSION, id: nextFrameId(), type: 'subscribe', topic, ts: Date.now() }
}

/** An unsubscribe frame for one topic. */
export function unsubscribeFrame(topic: string): Envelope {
  return { v: PROTOCOL_VERSION, id: nextFrameId(), type: 'unsubscribe', topic, ts: Date.now() }
}

/** A generic up-frame (defaults to a `command`) carrying a fresh id. */
export function publishFrame(topic: string, payload: unknown, type: Envelope['type'] = 'command'): Envelope {
  return { v: PROTOCOL_VERSION, id: nextFrameId(), type, topic, ts: Date.now(), payload }
}

/**
 * A liveness `pong` reply to the broker's heartbeat `ping`. The broker reaps any
 * connection idle past 2x its `heartbeatIntervalMs` with no inbound pong (§6
 * liveness) — receiving data frames does NOT reset that timer, so an operator
 * that only consumes (subscribes, never publishes) MUST pong or it is dropped
 * and forced to reconnect every ~60s. `reId` echoes the ping's id for symmetry;
 * the broker only needs the frame's type.
 */
export function pongFrame(reId: string): Envelope {
  return { v: PROTOCOL_VERSION, id: nextFrameId(), type: 'pong', reId, ts: Date.now() }
}

// ----------------------------------------------------------------- snapshot rows

/** The broker snapshot row shape (mirrors @libs/realtime SnapshotRow), kept
 *  loose here so a forward-compatible `seq` can ride along. */
export interface LiveSnapshotRow {
  robotId: string
  name?: string | null
  lng: number
  lat: number
  heading?: number | null
  battery?: number | null
  ctrlMode?: number | null
  recordedAt?: string
  seq?: number
}

/** Read `{ rows }` off a `snapshot` server frame, tolerating a missing payload. */
export function snapshotRows(frame: Envelope): LiveSnapshotRow[] {
  const payload = frame.payload as { rows?: unknown } | undefined
  const rows = payload?.rows

  return Array.isArray(rows) ? (rows as LiveSnapshotRow[]) : []
}

/** The broker alert-snapshot row shape (mirrors @libs/realtime AlertSnapshotRow). */
export interface LiveAlertSnapshotRow {
  alertId: string
  robotId: string
  name?: string | null
  type: string
  severity?: string | null
  identity?: string | null
  message?: string | null
  lng: number
  lat: number
  snapshotRef?: string | null
  status: string
  recordedAt?: string
  seq?: number
}

/** Read `payload.kind` off a `snapshot` frame; defaults to `'location'` for the
 *  pre-discriminator wire and for location snapshots. */
export function snapshotKind(frame: Envelope): 'location' | 'alert' {
  const kind = (frame.payload as { kind?: unknown } | undefined)?.kind

  return kind === 'alert' ? 'alert' : 'location'
}

/** Read `{ rows }` off an `alert`-kind `snapshot` frame, tolerating a missing payload. */
export function alertSnapshotRows(frame: Envelope): LiveAlertSnapshotRow[] {
  const payload = frame.payload as { rows?: unknown } | undefined
  const rows = payload?.rows

  return Array.isArray(rows) ? (rows as LiveAlertSnapshotRow[]) : []
}

// ----------------------------------------------------------------- seq dedup

/** The per-`(robotId, frameType)` sequence high-water tracker (§8). A live frame
 *  whose `seq` is `<=` the recorded high-water is a persist-then-fanout duplicate
 *  and must be dropped. Frames without a `seq` are never deduped (always kept). */
/**
 * How far BACKWARDS a sequence may jump before it is read as a restart rather than a duplicate.
 *
 * The two are genuinely different events and telling them apart is the whole job here. A duplicate
 * or a reorder arrives at or just below the mark; a publisher that restarted its counter drops to
 * 1, hundreds below. Treating a restart as duplicates is what froze a helmet's marker on the map:
 * every live frame was dropped until the new counter climbed past the old mark — about an hour for
 * a device reporting every 10s.
 *
 * The broker now keeps its sequence per DEVICE rather than per connection, so a helmet reconnect no
 * longer restarts it. A BROKER restart still does — every deploy — which is why this guard stays.
 */
const SEQ_RESTART_GAP = 64

export class SeqHighWater {
  private readonly marks = new Map<string, number>()

  private key(robotId: string, type: string): string {
    return `${robotId} ${type}`
  }

  /** Seed the high-water from a snapshot's rows (each row's `seq`, when present). */
  seedFromSnapshot(rows: LiveSnapshotRow[], types: readonly string[]): void {
    for (const row of rows) {
      if (typeof row.seq !== 'number') continue
      for (const type of types) this.bump(row.robotId, type, row.seq)
    }
  }

  private bump(robotId: string, type: string, seq: number): void {
    const k = this.key(robotId, type)
    const cur = this.marks.get(k)
    if (cur === undefined || seq > cur) this.marks.set(k, seq)
  }

  /**
   * Decide whether a live frame should be accepted. Returns true (accept) and
   * advances the high-water when the frame is new; returns false (drop) when the
   * frame's `seq` is at or below the recorded high-water. Frames without a `seq`
   * are always accepted (no dedup possible).
   */
  accept(robotId: string, type: string, seq: number | undefined): boolean {
    if (typeof seq !== 'number') return true
    const k = this.key(robotId, type)
    const cur = this.marks.get(k)

    if (cur !== undefined && seq <= cur) {
      // Two independent restart signals, because the gap rule alone has a blind spot.
      //
      //  * `seq === 1` is definitive: a counter only emits 1 as its first frame, so this is a
      //    restart however small the mark is. Without it, a mark of 10 rejects seq 1..10 and the
      //    marker stays frozen for the first ten frames — the same failure at smaller scale, and
      //    the band the gap rule can never see (any mark ≤ SEQ_RESTART_GAP).
      //  * A LARGE backwards jump catches a restart whose first frames were lost in transit.
      //
      // Anything else at or below the mark is a duplicate or a reorder, and is still dropped.
      const restarted = seq === 1 || cur - seq > SEQ_RESTART_GAP

      if (!restarted) return false
    }

    this.marks.set(k, seq)

    return true
  }

  /** Drop all marks (e.g. on reconnect, before re-snapshotting). */
  reset(): void {
    this.marks.clear()
  }
}

// ----------------------------------------------------------------- backoff

/** Capped exponential backoff with full jitter for reconnect (§10 resilience). */
export function reconnectDelayMs(attempt: number, baseMs = 500, capMs = 15_000): number {
  const exp = Math.min(capMs, baseMs * 2 ** Math.max(0, attempt))

  return Math.floor(Math.random() * exp)
}
